/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

// Note: This Promise polyfill is included for browser builds only to address compatibility issues
// with older browsers whose native Promise<T>implementation does not support .finally.

import PromiseHandler from './PromiseHandler';

class Promise<T> {
  _state: number;
  _handled: boolean;
  _value: T | unknown;
  _deferreds: Array<PromiseHandler<T>>;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  then(onFulfilled: Function | null, onRejected: Function | null): Promise<T>| void;

  constructor(callback: Function) {
    if (!(this instanceof Promise)) {
      throw new TypeError('Promises must be constructed via new');
    }

    if (typeof callback !== 'function') {
      throw new TypeError('not a function');
    }

    this._state = 0;
    this._handled = false;
    this._value = null;
    this._deferreds = [];

    this.doResolve(callback);
  }

  doResolve(callback: Function): void {
    let done = false;

    try {
      callback(
        (value: Promise<T>| null) => {
          if (done) {
            return;
          }

          done = true;
          this.promiseResolve(value);
        },
        (reason: Promise<T>| null) => {
          if (done) {
            return;
          }

          done = true;
          this.promiseReject(reason);
        }
      );
    } catch (error) {
      if (done) {
        return;
      }

      done = true;
      this.promiseReject(error);
    }
  }

  promiseResolve(newValue: Promise<T>| null | unknown): void {
    const promise = this as Promise<T>;

    try {
      // Promise<T>Resolution Procedure: https://github.com/Promises-aplus/Promises-spec#the-Promise-resolution-procedure
      if (newValue === promise) {
        throw new TypeError('A Promise cannot be resolved with it self.');
      }

      if (newValue && (typeof newValue === 'object' || typeof newValue === 'function')) {
        // Disabling a TS compiler check for next line to not do extra if statements
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const then = newValue.then;

        if (newValue instanceof Promise) {
          promise._state = 3;
          promise._value = newValue;
          promise.finale();

          return;
        } else if (typeof then === 'function') {
          promise.doResolve(function(): void {
            // eslint-disable-next-line prefer-rest-params
            then.apply(newValue, arguments as unknown as [onFulfilled: Function, onRejected: Function]);
          });

          return;
        }
      }

      promise._state = 1;
      promise._value = newValue;
      promise.finale();
    } catch (e) {
      promise.promiseReject(e);
    }
  }

  promiseReject(newValue: unknown): void {
    this._state = 2;
    this._value = newValue;
    this.finale();
  }

  finale(): void {
    if (this._state === 2 && this._deferreds.length === 0) {
      Promise._immediate(() => {
        if (!this._handled) {
          Promise._unhandledRejection(this._value);
        }
      });
    }

    for (let i = 0, len = this._deferreds.length; i < len; i++) {
      this.handle(this._deferreds[i]);
    }

    this._deferreds = [];
  }

  handle(deferred: PromiseHandler<T>): void {
    let promise = this as Promise<T>;

    while (promise._state === 3) {
      promise = promise._value as Promise<T>;
    }

    if (promise._state === 0) {
      promise._deferreds.push(deferred);

      return;
    }

    promise._handled = true;
    Promise._immediate(() => {
      const callback = promise._state === 1 ? deferred.onFulfilled : deferred.onRejected;

      if (callback === null) {
        if (promise._state === 1) {
          deferred.promise.promiseResolve(promise._value);
        } else {
          deferred.promise.promiseReject(promise._value);
        }

        return;
      }

      let result;

      try {
        result = callback(promise._value);
      } catch (e) {
        deferred.promise.promiseReject(e);

        return;
      }

      deferred.promise.promiseResolve(result);
    });
  }

  static all(arr: Array<Promise<unknown>>): Promise<unknown> {
    return new Promise((resolve: Function, reject: Function) => {
      if (!Array.isArray(arr)) {
        return reject(new TypeError('Promise.all accepts an array'));
      }

      const args = Array.prototype.slice.call(arr);

      if (args.length === 0) {
        return resolve([]);
      }

      let remaining = args.length;
      const manageResponse = (i: number, value: Promise<unknown>): void => {
        try {
          if (value && (typeof value === 'object' || typeof value === 'function')) {
            const then = value.then;

            if (typeof then === 'function') {
              then.call(
                value,
                (value: Promise<unknown>) => manageResponse(i, value),
                reject
              );

              return;
            }
          }

          args[i] = value;

          if (--remaining === 0) {
            resolve(args);
          }
        } catch (error) {
          reject(error);
        }
      };

      for (let i = 0; i < args.length; i++) {
        manageResponse(i, args[i]);
      }
    });
  }

  static resolve(value: unknown): Promise<unknown> {
    if (value && typeof value === 'object' && value.constructor === Promise) {
      return value;
    }

    return new Promise((resolve: Function) => resolve(value));
  }

  static reject(value: unknown): Promise<unknown> {
    return new Promise((_: undefined, reject: Function) => reject(value));
  }

  static race(arr: Array<Promise<unknown>>): Promise<unknown> {
    return new Promise((resolve: Function, reject: Function) => {
      if (!Array.isArray(arr)) {
        return reject(new TypeError('Promise.race accepts an array'));
      }

      for (let i = 0, len = arr.length; i < len; i++) {
        Promise.resolve(arr[i]).then(resolve, reject);
      }
    });
  }

  static _immediate(callback: Function): void {
    const ignored = window.setTimeout(callback, 0);
  }

  static _unhandledRejection(error: Promise<unknown>| null | unknown): void {
    if (typeof console !== 'undefined' && console) {
      console.warn('Possible Unhandled Promise Rejection:', error); // eslint-disable-line no-console
    }
  }
}

// Disabling a TS compiler check for unsupported prototype change
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Promise.prototype['catch'] = function(onRejected: Function): unknown {
  return this.then(null, onRejected);
};
// Disabling a TS compiler check for unsupported prototype change
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Promise.prototype['then'] = function(onFulfilled: Function, onRejected: Function): Promise<unknown>| void {
  const promise = new Promise((): null => null);

  this.handle(new PromiseHandler<unknown>(onFulfilled, onRejected, promise));

  return promise;
};
// Disabling a TS compiler check for unsupported prototype change
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Promise.prototype['finally'] = function(callback: Function): Promise<unknown>| void {
  return this.then(
    (value: Promise<unknown>| undefined) => Promise.resolve(callback()).then(() => value, (): null => null),
    (reason: Error | string) => Promise.resolve(callback()).then(() => Promise.reject(reason), (): null => null)
  );
};

export default Promise;